import { Call, CallDecline } from '@/assets/icons/call';
import { ContactUnknownAvatar } from '@/assets/icons/contacts';
import { Button } from '@/components/ui/design-system';
import { LoadingDots } from '@/components/ui/spinner';
import { useTranslation } from '@/hooks/use-translation';
import { getInitials } from '@/lib/conversation';
import { isDesktop } from '@/lib/platform';
import { cn } from '@/lib/utils';
import { formatPhoneNumber } from '@packages/corekit/src/utils/phoneNumbers';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/desktop/incoming-call')({
  component: IncomingCallNotification,
});

interface CallerData {
  callerName: string;
  callerNumber?: string;
  userLine?: {
    lineName?: string;
    lineNumber?: string;
    originalToNumber?: string;
    originalToName?: string;
  };
}

function IncomingCallNotification() {
  const { t } = useTranslation('desktop_ui');
  const [callerData, setCallerData] = useState<CallerData>({
    callerName: t('calls.unknown', { ns: 'desktop_calls' }),
  });
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const cleanup = window.incomingCallWindowAPI?.onData((data) => {
      setCallerData(data);
    });

    window.incomingCallWindowAPI?.requestData();

    return () => {
      cleanup?.();
    };
  }, []);

  const handleAnswer = () => {
    if (isProcessing) {
      return;
    }
    setIsProcessing(true);
    window.incomingCallWindowAPI?.answer();
  };

  const handleReject = () => {
    if (isProcessing) {
      return;
    }
    setIsProcessing(true);
    window.incomingCallWindowAPI?.reject();
  };

  if (isDesktop() === false) {
    return null;
  }

  const useAvatar =
    callerData.callerName &&
    callerData.callerName !== t('calls.unknown', { ns: 'desktop_calls' }) &&
    /^\+?[\d\s\(\)\-]+$/.test(callerData.callerName) === false;

  return (
    <div className="h-screen w-screen bg-transparent">
      <div
        className="flex flex-col gap-3 bg-gradient-to-b from-[var(--color-gradient-green-start)] to-[var(--color-gradient-green-end)] py-4 shadow-xl h-full items-center justify-between"
        style={{ WebkitAppRegion: 'drag' } as React.CSSProperties}
      >
        {
          <div
            className={cn(
              'rounded-t-2xl flex flex-row items-center justify-center gap-2 border-b-1 border-b-white/10 px-4 pb-2 w-full',
            )}
          >
            {callerData.userLine?.lineName && (
              <span className="font-inter font-medium text-white line-clamp-1">
                {callerData.userLine?.lineName}
              </span>
            )}
            {callerData.userLine?.lineNumber && (
              <span className="font-inter font-medium text-white/60 shrink-0">
                {formatPhoneNumber(callerData.userLine.lineNumber, { format: 'national' }).number}
              </span>
            )}
            {(callerData.userLine?.originalToName || callerData.userLine?.originalToNumber) && (
              <span className="font-inter font-medium text-white/40 line-clamp-1">
                {t('components.incomingCall.viaAlias', {
                  alias: [
                    callerData.userLine.originalToName,
                    callerData.userLine.originalToNumber &&
                      formatPhoneNumber(callerData.userLine.originalToNumber, {
                        format: 'national',
                      }).number,
                  ]
                    .filter(Boolean)
                    .join(' '),
                })}
              </span>
            )}
            {callerData.userLine === undefined && <LoadingDots />}
          </div>
        }

        <div className="flex flex-col items-center gap-1 px-4 text-center">
          <span className="text-body-13 text-white/60">{t('components.incomingCall.title')}</span>
          <div className="flex items-center gap-3 px-3">
            <div className="flex size-8 items-center justify-center rounded-full bg-white/20 text-body-14 font-semibold text-white shrink-0">
              {useAvatar ? (
                getInitials(callerData.callerName)
              ) : (
                <ContactUnknownAvatar className="size-8 fill-white" />
              )}
            </div>
            <span className="text-center text-body-20 font-semibold text-white line-clamp-1">
              {callerData.callerName}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div
          className="flex flex-row w-full gap-4 px-4"
          style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
        >
          <Button
            className="flex flex-1 items-center gap-2 rounded-full bg-danger px-8 text-body-14 text-white hover:bg-danger/80 cursor-pointer"
            onClick={handleReject}
          >
            <CallDecline className="size-5 fill-white" />
            <span>{t('components.incomingCall.decline')}</span>
          </Button>
          <Button
            className="flex flex-1 items-center gap-2 rounded-full bg-extra-answer px-8 text-body-14 text-white hover:bg-extra-answer/80 cursor-pointer"
            onClick={handleAnswer}
            autoFocus
          >
            <Call className="fill-white" />
            <span>{t('components.incomingCall.accept')}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
